import React from 'react';
import { Modal } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import ReactDOM from 'react-dom';
import {
  AlertTriangle,
  ArrowUpRight,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Clock,
  Download,
  ExternalLink,
  Expand,
  FileText,
  Filter,
  Lock,
  Plus,
  RefreshCw,
  Search,
  Shrink,
  SlidersHorizontal,
  Wallet as WalletIcon,
  X,
  Gavel,
  BanknoteArrowDownIcon,
} from 'lucide-react';

import '../../../../styles/components/my-wallet.scss';
import i18n from '../../../../i18n';
import { getLoggedUser } from '../../../../state/user';
import { getAllOrganization } from '../../../../models/organization';
import { getGroupsList } from '../../../../models/groups';
import { getUserInfo } from '../../../../models/users';
import { createWallet } from '../../../../models/wallet';
import { SideBar } from '../../sidebar';
import HeaderBanner from '../../../common/HeaderBanner';
import { TRANSACTION_TYPES, USER_TRANSACTION_STATUS } from '../../../../constants/status';
import { getPersonalTransactions } from './services/transactionService';
import { imageUrl } from '../../../../constants/global';
import { OMR } from '../../../common/icons';
import { formatDate } from '../../../../utils/common';

const user = getLoggedUser();

/* -------------------------------------------------------------------------- */
/*                                   Config                                   */
/* -------------------------------------------------------------------------- */

const ROWS_PER_PAGE_OPTIONS = [10, 15, 25, 50, 100]; // API max is 100
const EXPORT_PAGE_SIZE = 100;
const DEFAULT_LIST = { page: 1, limit: 10 };

// `type` values accepted by GET /personal
const TYPE_FILTERS = {
  deposit: 'Deposit',
  auction_payment: 'Auction Payment',
  hold: 'Hold',
  withdraw: 'Withdraw',
};

// `method` values accepted by GET /personal
const METHOD_FILTERS = {
  online: 'Online',
  offline: 'Offline',
  wallet: 'Wallet',
};

// URL keys use the same names as the API params.
const URL_FILTER_KEYS = ['search', 'type', 'method', 'client', 'group', 'startDate', 'endDate'];
const ADVANCED_FILTER_KEYS = ['method', 'client', 'group', 'startDate', 'endDate'];

// Old links used different names; they are read once and rewritten.
const LEGACY_URL_KEYS = {
  trans_type: 'type',
  pay_type: 'method',
  fdate: 'startDate',
  tdate: 'endDate',
};
const LEGACY_TYPE_VALUES = { client_due: 'auction_payment' };

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const ID_RE = /^\d+$/;
const IMAGE_RE = /\.(png|jpe?g|gif|webp|bmp|svg)(\?.*)?$/i;

/* -------------------------------------------------------------------------- */
/*                                   Helpers                                  */
/* -------------------------------------------------------------------------- */

const t = (key, fallback) => {
  const value = i18n.t(key);
  return value && value !== key ? value : fallback || key;
};

// Drops values the API would reject with a 400 instead of sending them.
const sanitizeFilter = (key, raw) => {
  const value = String(raw ?? '').trim();
  if (!value) return '';
  switch (key) {
    case 'type': {
      const v = LEGACY_TYPE_VALUES[value] || value;
      return TYPE_FILTERS[v] ? v : '';
    }
    case 'method':
      return METHOD_FILTERS[value] ? value : '';
    case 'client':
    case 'group':
      return ID_RE.test(value) ? value : '';
    case 'startDate':
    case 'endDate':
      return DATE_RE.test(value) ? value : '';
    case 'search':
      return value.slice(0, 100);
    default:
      return value;
  }
};

const readListFromUrl = () => {
  const params = new URLSearchParams(window.location.search);
  const list = { ...DEFAULT_LIST };

  URL_FILTER_KEYS.forEach((key) => {
    const legacyKey = Object.keys(LEGACY_URL_KEYS).find((k) => LEGACY_URL_KEYS[k] === key);
    const value = sanitizeFilter(key, params.get(key) ?? (legacyKey ? params.get(legacyKey) : ''));
    if (value) list[key] = value;
  });

  // endDate must be on/after startDate (API rule) → drop it otherwise.
  if (list.startDate && list.endDate && list.endDate < list.startDate) delete list.endDate;

  const page = parseInt(params.get('page'), 10);
  if (page > 0) list.page = page;

  const limit = parseInt(params.get('limit'), 10);
  if (ROWS_PER_PAGE_OPTIONS.includes(limit)) list.limit = limit;

  return list;
};

const writeListToUrl = (list) => {
  const params = new URLSearchParams(window.location.search);

  Object.keys(LEGACY_URL_KEYS).forEach((k) => params.delete(k));
  URL_FILTER_KEYS.forEach((key) => {
    if (list[key]) params.set(key, list[key]);
    else params.delete(key);
  });

  if (list.page && list.page !== DEFAULT_LIST.page) params.set('page', list.page);
  else params.delete('page');

  if (list.limit && list.limit !== DEFAULT_LIST.limit) params.set('limit', list.limit);
  else params.delete('limit');

  const query = params.toString();
  window.history.replaceState(
    window.history.state,
    '',
    `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`
  );
};

// State → query params for GET /personal (empty values are left out).
const buildApiParams = (list, overrides = {}) => {
  const merged = { ...list, ...overrides };
  const params = { page: merged.page || 1, limit: merged.limit || DEFAULT_LIST.limit };
  URL_FILTER_KEYS.forEach((key) => {
    const value = sanitizeFilter(key, merged[key]);
    if (value) params[key] = value;
  });
  return params;
};

const toNumber = (v) => {
  const n = parseFloat(String(v ?? '').replace(/,/g, ''));
  return Number.isFinite(n) ? n : 0;
};

const fmt = (v) =>
  toNumber(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// const formatDate = (value) => {
//   if (!value) return '—';
//   const iso = /^\d{4}-\d{2}-\d{2}/.exec(String(value));
//   if (iso) return iso[0];
//   const d = new Date(value);
//   if (Number.isNaN(d.getTime())) return String(value);
//   const pad = (n) => String(n).padStart(2, '0');
//   return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
// };

const timeAgo = (date) => {
  if (!date) return '';
  const mins = Math.floor((Date.now() - date.getTime()) / 60000);
  if (mins < 1) return t('just_now', 'just now');
  if (mins < 60) return `${mins} ${t('min_ago', 'min ago')}`;
  return `${Math.floor(mins / 60)} ${t('hours_ago', 'h ago')}`;
};

const typeLabel = (row) => TRANSACTION_TYPES[row.type]?.text || row.type || '—';
const statusLabel = (row) =>
  row.status ? USER_TRANSACTION_STATUS[row.status]?.text || row.status : '—';
const methodLabel = (method) => (method ? t(method, METHOD_FILTERS[method] || method) : '');

const typeVisual = (row) => {
  const key = `${row.type || ''} ${typeLabel(row)}`.toLowerCase();
  if (key.includes('enroll')) return { Icon: Lock, tone: 'red' };
  if (key.includes('withdraw')) return { Icon: BanknoteArrowDownIcon, tone: 'blue' };
  if (key.includes('wallet_recharge')) return { Icon: WalletIcon, tone: 'green' };
  if (key.includes('client_payment')) return { Icon: Gavel, tone: 'amber' };
  return { Icon: FileText, tone: 'teal' };
};

const statusTone = (row) => {
  const s = statusLabel(row).toLowerCase();
  if (s.includes('request') || s.includes('process')) return 'info';
  if (s.includes('approv') || s.includes('success') || s.includes('complete') || s.includes('paid'))
    return 'success';
  if (s.includes('reject') || s.includes('fail') || s.includes('cancel') || s.includes('declin'))
    return 'danger';
  if (s.includes('pend') || s.includes('hold') || s.includes('wait')) return 'warning';
  return 'neutral';
};

/* ------------------------------ Receipt files ----------------------------- */

// Folder each receipt field is stored in (relative to imageUrl).
const RECEIPT_DIRS = {
  wallet: 'uploads/wallet/receipt/',
  enroll: 'uploads/enrolls/receipt/',
};

// `${imageUrl}${dir}${fileName}`. Full URLs from the API are used as-is.
const fileUrl = (name, dir) => {
  if (!name) return '';
  const s = String(name).trim();
  if (!s) return '';
  if (/^(https?:)?\/\//i.test(s) || s.startsWith('data:') || s.startsWith('blob:')) return s;

  const file = s.replace(/^\/+/, '');
  // Don't double the folder if the API already sent "uploads/wallet/receipt/x.jpg".
  const path = file.startsWith(dir) ? file : `${dir}${file}`;
  return `${imageUrl || ''}${path}`;
};

const pickFilePath = (item) => {
  if (!item) return '';
  if (typeof item === 'string') return item;
  return (
    item.receipt_image_url ||
    item.receipt_url ||
    item.file_url ||
    item.url ||
    item.file ||
    item.file_receipt ||
    item.path ||
    item.image ||
    ''
  );
};

const asArray = (v) => (Array.isArray(v) ? v : v ? [v] : []);

// Every receipt/attachment on a transaction, de-duplicated.
const getReceiptFiles = (row) => {
  const files = [];
  const add = (name, dir, label) => {
    const url = fileUrl(name, dir);
    if (!url || files.some((f) => f.url === url)) return;
    files.push({ url, label, isImage: IMAGE_RE.test(url) });
  };

  add(row.file_receipt, RECEIPT_DIRS.wallet, t('receipt', 'Receipt'));
  add(row.enroll_receipt, RECEIPT_DIRS.enroll, t('enroll_receipt', 'Enrollment receipt'));
  asArray(row.payment_receipts).forEach((r) =>
    add(
      pickFilePath(r),
      RECEIPT_DIRS.wallet,
      [
        t('payment_receipt', 'Payment receipt'),
        r && r.receipt_number,
        r && r.amount && `${fmt(r.amount)} OMR`,
      ]
        .filter(Boolean)
        .join(' · ')
    )
  );

  return files;
};

// 1 … 4 5 6 … 20
const pageItems = (current, total) => {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const items = [1];
  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);
  if (start > 2) items.push('gap-start');
  for (let p = start; p <= end; p += 1) items.push(p);
  if (end < total - 1) items.push('gap-end');
  items.push(total);
  return items;
};

const csvCell = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;

// Keys match the API `type` filter values so stat cards can filter by them.
const EMPTY_SUMMARY = { deposit: 0, auction_payment: 0, hold: 0, withdraw: 0, balance: null };

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */

export class MyWallet extends React.Component {
  constructor(props) {
    super(props);
    const initialList = readListFromUrl();

    this.state = {
      hideSidebar: false,

      // wallet
      walletLoading: false,
      userInfo: {},
      lastUpdated: null,

      // transactions
      isLoading: false,
      loadError: null,
      transactions: [],
      totalRows: 0,
      totalPages: 1,
      summary: { ...EMPTY_SUMMARY },
      list: initialList,
      searchInput: initialList.search || '',
      showFilters: ADVANCED_FILTER_KEYS.some((k) => initialList[k]),
      exporting: false,

      // filter options
      clients: [],
      groups: [],

      // receipt preview
      preview: null, // { row, files, index }

      // withdraw
      showWithdraw: false,
      input: {},
      errors: {},
      isWithdrawSending: false,
    };

    this.searchTimer = null;
    this.requestSeq = 0; // ignores responses from older requests
  }

  componentDidMount() {
    window.scrollTo(0, 0);
    writeListToUrl(this.state.list); // normalise legacy / invalid URL params
    this.loadAllOrganisation();
    this.loadAllGroups();
    this.checkUserWallet();
    this.loadAllTransactions();
    this.tick = setInterval(() => this.forceUpdate(), 30000);
  }

  componentWillUnmount() {
    clearInterval(this.tick);
    clearTimeout(this.searchTimer);
    this.requestSeq += 1;
  }

  /* --------------------------------- Data --------------------------------- */

  async loadAllOrganisation() {
    try {
      const response = await getAllOrganization();
      this.setState({ clients: (response && response.data) || [] });
    } catch (error) {
      console.error(error);
    }
  }

  async loadAllGroups() {
    try {
      const { list } = this.state;
      const q = list.client ? `organization=${list.client}` : '';
      const response = await getGroupsList(q);
      this.setState({ groups: (response && response.data) || [] });
    } catch (error) {
      console.error(error);
    }
  }

  async checkUserWallet() {
    this.setState({ walletLoading: true });
    try {
      const response = await getUserInfo(user.id);
      if (response && response.success) {
        const data = response.data;
        this.setState({
          userInfo: data,
          lastUpdated: new Date(),
          input: {
            bank: data.bank || '',
            beneficiary: data.beneficiary || '',
            accountNumber: data.account_number || '',
            amount: data.wallet_amount || '',
          },
        });
      }
    } catch (error) {
      console.error(error);
    } finally {
      this.setState({ walletLoading: false });
    }
  }

  async loadAllTransactions() {
    const requestId = ++this.requestSeq;
    const { list } = this.state;
    this.setState({ isLoading: true, loadError: null });

    try {
      const response = await getPersonalTransactions(buildApiParams(list));
      if (requestId !== this.requestSeq) return;

      if (!response || response.success === false) {
        throw new Error((response && response.message) || 'Failed to load transactions');
      }

      const meta = response.meta || {};
      const total = toNumber(meta.total);
      const pages = Math.max(1, toNumber(meta.pages) || Math.ceil(total / list.limit));

      // Page from an old link can be past the end → go to the last real page.
      if (total > 0 && list.page > pages) {
        this.setListAndReload({ page: pages });
        return;
      }

      this.setState({
        transactions: Array.isArray(response.data) ? response.data : [],
        totalRows: total,
        totalPages: pages,
        summary: {
          deposit: meta.credit_sum ?? meta.deposit_sum ?? 0,
          auction_payment: meta.auction_payment_sum ?? 0,
          hold: meta.hold_sum ?? 0,
          withdraw: meta.debit_sum ?? meta.withdraw_sum ?? 0,
          balance: meta.balance ?? null,
        },
      });
    } catch (error) {
      if (requestId !== this.requestSeq) return;
      console.error(error);
      this.setState({
        transactions: [],
        totalRows: 0,
        totalPages: 1,
        loadError: (error && error.message) || t('load_failed', 'Could not load transactions.'),
      });
    } finally {
      if (requestId === this.requestSeq) this.setState({ isLoading: false });
    }
  }

  refreshAll = () => {
    this.checkUserWallet();
    this.loadAllTransactions();
  };

  /* ------------------------------- Filtering ------------------------------ */

  setListAndReload = (changes, { reloadGroups = false } = {}) => {
    this.setState(
      (prev) => ({ list: { ...prev.list, ...changes } }),
      () => {
        writeListToUrl(this.state.list);
        if (reloadGroups) this.loadAllGroups();
        this.loadAllTransactions();
      }
    );
  };

  // Any filter change returns to page 1.
  setFilter = (name, value) => {
    const { list } = this.state;
    const clean = sanitizeFilter(name, value);
    const changes = { [name]: clean, page: 1 };

    if (name === 'client') changes.group = '';
    if (name === 'startDate' && clean && list.endDate && list.endDate < clean) changes.endDate = '';
    if (name === 'endDate' && clean && list.startDate && clean < list.startDate)
      changes.startDate = '';

    this.setListAndReload(changes, { reloadGroups: name === 'client' });
  };

  handleFilterChange = (event) => {
    const { name, value } = event.target;
    this.setFilter(name, value);
  };

  // Search is typed into local state and sent after a pause.
  handleSearchInput = (event) => {
    const { value } = event.target;
    this.setState({ searchInput: value });
    clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(() => {
      if ((this.state.list.search || '') !== value.trim()) this.setFilter('search', value);
    }, 400);
  };

  clearSearch = () => {
    clearTimeout(this.searchTimer);
    this.setState({ searchInput: '' });
    if (this.state.list.search) this.setFilter('search', '');
  };

  clearFilters = () => {
    clearTimeout(this.searchTimer);
    const hadClient = !!this.state.list.client;
    const cleared = URL_FILTER_KEYS.reduce((acc, k) => ({ ...acc, [k]: '' }), { page: 1 });
    this.setState({ searchInput: '' });
    this.setListAndReload(cleared, { reloadGroups: hadClient });
  };

  activeFilterCount() {
    return ADVANCED_FILTER_KEYS.filter((k) => this.state.list[k]).length;
  }

  hasAnyFilter() {
    return URL_FILTER_KEYS.some((k) => this.state.list[k]);
  }

  handleStatClick = (type) => {
    this.setFilter('type', this.state.list.type === type ? '' : type);
  };

  typeFilterLabel(type) {
    return t(type, TYPE_FILTERS[type] || type);
  }

  clientName(id) {
    const c = this.state.clients.find((x) => String(x.id) === String(id));
    return c ? c.organization_name : `#${id}`;
  }

  groupName(id) {
    const g = this.state.groups.find((x) => String(x.id) === String(id));
    return g ? g.group_name : `#${id}`;
  }

  /* ------------------------------ Pagination ------------------------------ */

  goToPage = (page) => {
    const next = Math.min(Math.max(1, page), this.state.totalPages);
    if (next === this.state.list.page || this.state.isLoading) return;
    this.setListAndReload({ page: next });
  };

  handleLimitChange = (e) => {
    this.setListAndReload({ limit: Number(e.target.value), page: 1 });
  };

  /* -------------------------------- Export -------------------------------- */

  // Fetches every page that matches the current filters (API limit is 100/page).
  handleExport = async () => {
    this.setState({ exporting: true });
    const rows = [];

    try {
      let page = 1;
      let pages = 1;
      do {
        // eslint-disable-next-line no-await-in-loop
        const response = await getPersonalTransactions(
          buildApiParams(this.state.list, { page, limit: EXPORT_PAGE_SIZE })
        );
        if (!response || response.success === false) throw new Error('Export failed');
        rows.push(...(Array.isArray(response.data) ? response.data : []));
        pages = Math.max(1, toNumber(response.meta && response.meta.pages));
        page += 1;
      } while (page <= pages);
    } catch (error) {
      console.error(error);
      toast.error(t('export_failed'));
      this.setState({ exporting: false });
      return;
    }

    const header = [
      'ID',
      'Transaction',
      'Reference',
      'Receipt No.',
      'Credit',
      'Debit',
      'Type',
      'Method',
      'Status',
      'Group',
      'Date',
      'Receipt Files',
    ];
    const lines = rows.map((r) =>
      [
        r.id,
        r.transaction_id,
        r.reference,
        r.receipt_number,
        toNumber(r.credit) ? toNumber(r.credit).toFixed(3) : '',
        toNumber(r.debit) ? toNumber(r.debit).toFixed(3) : '',
        typeLabel(r),
        methodLabel(r.method),
        statusLabel(r),
        r.group && r.group.group_name,
        formatDate(r.created_at),
        getReceiptFiles(r)
          .map((f) => f.url)
          .join(' | '),
      ]
        .map(csvCell)
        .join(',')
    );

    const blob = new Blob([`\uFEFF${[header.map(csvCell).join(','), ...lines].join('\n')}`], {
      type: 'text/csv;charset=utf-8;',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `wallet-transactions-${formatDate(new Date())}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);

    this.setState({ exporting: false });
  };

  /* ---------------------------- Receipt preview --------------------------- */

  openPreview = (row, files, index = 0) => this.setState({ preview: { row, files, index } });

  closePreview = () => this.setState({ preview: null });

  stepPreview = (delta) => {
    this.setState((prev) => {
      if (!prev.preview) return null;
      const count = prev.preview.files.length;
      return { preview: { ...prev.preview, index: (prev.preview.index + delta + count) % count } };
    });
  };

  /* -------------------------------- Withdraw ------------------------------ */

  openWithdraw = () => {
    const { userInfo } = this.state;
    if (toNumber(userInfo.wallet_amount) < 1) {
      toast.warning(t('insufficient_balance'));
      return;
    }
    this.setState((prev) => ({
      showWithdraw: true,
      errors: {},
      input: {
        ...prev.input,
        bank: prev.input.bank ?? userInfo.bank ?? '',
        beneficiary: prev.input.beneficiary ?? userInfo.beneficiary ?? '',
        accountNumber: prev.input.accountNumber ?? userInfo.account_number ?? '',
        amount: userInfo.wallet_amount || '',
      },
    }));
  };

  closeWithdraw = () => {
    if (!this.state.isWithdrawSending) this.setState({ showWithdraw: false });
  };

  handleChange = (event) => {
    const { name, value } = event.target;
    const { userInfo } = this.state;

    if (name === 'amount') {
      if (value !== '' && !/^\d*\.?\d{0,3}$/.test(value)) return;
      if (toNumber(value) > toNumber(userInfo.wallet_amount)) {
        this.setState((prev) => ({
          errors: {
            ...prev.errors,
            amount: `${t('amount_exceeds_balance', 'Amount should not exceed')} ${fmt(
              userInfo.wallet_amount
            )}`,
          },
        }));
        return;
      }
    }

    this.setState((prev) => ({
      input: { ...prev.input, [name]: value },
      errors: { ...prev.errors, [name]: undefined },
    }));
  };

  setMaxAmount = () => {
    const { userInfo } = this.state;
    this.setState((prev) => ({
      input: { ...prev.input, amount: String(userInfo.wallet_amount || '') },
      errors: { ...prev.errors, amount: undefined },
    }));
  };

  validate() {
    const { input, userInfo } = this.state;
    const errors = {};

    if (!String(input.bank || '').trim()) errors.bank = t('bank_required', 'Bank is required');
    if (!String(input.beneficiary || '').trim())
      errors.beneficiary = t('beneficiary_required', 'Beneficiary is required');
    if (!String(input.accountNumber || '').trim())
      errors.accountNumber = t('account_number_required', 'Account number is required');

    if (!input.amount) {
      errors.amount = t('amount_required', 'Amount is required');
    } else if (toNumber(input.amount) <= 0) {
      errors.amount = t('amount_invalid', 'Enter a valid amount');
    } else if (toNumber(input.amount) > toNumber(userInfo.wallet_amount)) {
      errors.amount = t('amount_exceeds_balance', 'Amount should not exceed the balance');
    }

    this.setState({ errors });
    return Object.keys(errors).length === 0;
  }

  handleSubmit = async (event) => {
    event.preventDefault();
    if (!this.validate()) return;

    this.setState({ isWithdrawSending: true });
    const { input } = this.state;

    try {
      // Payload keys stay snake_case — they are the API's field names.
      const payload = {
        no_balance_update: false,
        user: user.id,
        type: 'withdraw',
        debit: input.amount,
        credit: 0,
        status: 'W',
        method: 'offline',
        bank: input.bank,
        beneficiary: input.beneficiary,
        account_number: input.accountNumber,
      };

      const response = await createWallet(payload);

      if (response && response.success) {
        this.setState({ showWithdraw: false, errors: {} });
        toast.success(t('withdraw_display_message'), { autoClose: 10000 });
        this.refreshAll();
      } else {
        const lines = [(response && response.message) || t('something_went_wrong')];
        if (response && response.data && typeof response.data === 'object') {
          Object.values(response.data).forEach((v) => lines.push(String(v)));
        }
        toast.error(<div style={{ whiteSpace: 'pre-line' }}>{lines.join('\n')}</div>);
      }
    } catch (err) {
      console.error(err);
      toast.error(t('unexpected_error'));
    } finally {
      this.setState({ isWithdrawSending: false });
    }
  };

  /* --------------------------------- Render ------------------------------- */

  renderBalanceCard() {
    const { userInfo, walletLoading, lastUpdated, summary } = this.state;
    const balance = userInfo.wallet_amount ?? summary.balance;
    const canWithdraw = toNumber(userInfo.wallet_amount) >= 1;

    return (
      <div className="wlt-balance">
        <div className="wlt-balance__label">
          {t('available_balance')}
          <button
            type="button"
            className={`wlt-balance__refresh ${walletLoading ? 'is-spinning' : ''}`}
            onClick={this.refreshAll}
            aria-label={t('refresh')}
          >
            <RefreshCw size={12} />
          </button>
        </div>

        <div className="wlt-balance__amount">
          <span className="wlt-num">{fmt(balance)}</span>
          <span className="wlt-balance__currency">
            <OMR size={16} color="#f6f6f6" />
          </span>
        </div>

        <div className="wlt-balance__updated">
          <Clock size={13} />
          {lastUpdated ? `${t('updated')} ${timeAgo(lastUpdated)}` : t('loading')}
        </div>

        <div className="wlt-balance__actions">
          <Link to="/wallet-manage" className="wlt-btn wlt-btn--lime">
            <Plus size={16} />
            {t('add_fund')}
          </Link>
          <button
            type="button"
            className="wlt-btn wlt-btn--glass"
            onClick={this.openWithdraw}
            disabled={!canWithdraw}
          >
            <BanknoteArrowDownIcon size={16} />
            {t('withdraw')}
          </button>
        </div>
      </div>
    );
  }

  renderStat(type, tone, Icon, label) {
    const active = this.state.list.type === type;
    const value = this.state.summary[type];

    return (
      <button
        type="button"
        className={`wlt-stat wlt-stat--clickable ${active ? 'is-active' : ''}`}
        onClick={() => this.handleStatClick(type)}
        aria-pressed={active}
        title={active ? t('clear_filter') : `${t('show_only')} ${label}`}
      >
        <span className={`wlt-stat__icon wlt-tone--${tone}`}>
          <Icon size={16} />
        </span>
        <span className="wlt-stat__body">
          <span className="wlt-stat__label">{label}</span>
          <span className="wlt-stat__value">
            <span className="wlt-num">{fmt(value)}</span>
            <small>{t('omr', 'OMR')}</small>
          </span>
        </span>
      </button>
    );
  }

  // Removable chips for every active filter.
  renderActiveFilterBar() {
    if (!this.hasAnyFilter()) return null;
    const { list } = this.state;
    const chips = [];

    if (list.type)
      chips.push({
        key: 'type',
        label: this.typeFilterLabel(list.type),
        clear: () => this.setFilter('type', ''),
      });
    if (list.search)
      chips.push({ key: 'search', label: `“${list.search}”`, clear: this.clearSearch });
    if (list.method)
      chips.push({
        key: 'method',
        label: methodLabel(list.method),
        clear: () => this.setFilter('method', ''),
      });
    if (list.client)
      chips.push({
        key: 'client',
        label: this.clientName(list.client),
        clear: () => this.setFilter('client', ''),
      });
    if (list.group)
      chips.push({
        key: 'group',
        label: this.groupName(list.group),
        clear: () => this.setFilter('group', ''),
      });
    if (list.startDate || list.endDate)
      chips.push({
        key: 'dates',
        label: `${list.startDate || '…'} → ${list.endDate || '…'}`,
        clear: () => this.setListAndReload({ startDate: '', endDate: '', page: 1 }),
      });

    return (
      <div className="wlt-filterbar" role="status">
        <Filter size={15} />
        <div className="wlt-chips">
          {chips.map((chip) => (
            <span className="wlt-chip" key={chip.key}>
              {chip.label}
              <button
                type="button"
                onClick={chip.clear}
                aria-label={`${t('remove', 'Remove')} ${chip.label}`}
              >
                <X size={12} />
              </button>
            </span>
          ))}
        </div>
        <button type="button" className="wlt-filterbar__clear" onClick={this.clearFilters}>
          {t('clear_all', 'Clear all')}
        </button>
      </div>
    );
  }

  renderFilters() {
    const { list, clients, groups, showFilters } = this.state;
    if (!showFilters) return null;

    return (
      <div className="wlt-filters">
        <div className="wlt-field">
          <label htmlFor="wlt-f-start">{t('from_date', 'From date')}</label>
          <input
            id="wlt-f-start"
            type="date"
            className="wlt-input"
            name="startDate"
            value={list.startDate || ''}
            max={list.endDate || undefined}
            onChange={this.handleFilterChange}
          />
        </div>
        <div className="wlt-field">
          <label htmlFor="wlt-f-end">{t('to_date', 'To date')}</label>
          <input
            id="wlt-f-end"
            type="date"
            className="wlt-input"
            name="endDate"
            value={list.endDate || ''}
            min={list.startDate || undefined}
            onChange={this.handleFilterChange}
          />
        </div>
        <div className="wlt-field">
          <label htmlFor="wlt-f-method">{t('pay_type', 'Pay type')}</label>
          <select
            id="wlt-f-method"
            className="wlt-input"
            name="method"
            value={list.method || ''}
            onChange={this.handleFilterChange}
          >
            <option value="">{t('all', 'All')}</option>
            {Object.keys(METHOD_FILTERS).map((m) => (
              <option key={m} value={m}>
                {methodLabel(m)}
              </option>
            ))}
          </select>
        </div>
        <div className="wlt-field">
          <label htmlFor="wlt-f-client">{t('client', 'Client')}</label>
          <select
            id="wlt-f-client"
            className="wlt-input"
            name="client"
            value={list.client || ''}
            onChange={this.handleFilterChange}
          >
            <option value="">{t('select_client', 'All clients')}</option>
            {clients.map((item) => (
              <option value={item.id} key={item.id}>
                {item.organization_name}
              </option>
            ))}
          </select>
        </div>
        <div className="wlt-field">
          <label htmlFor="wlt-f-group">{t('group', 'Group')}</label>
          <select
            id="wlt-f-group"
            className="wlt-input"
            name="group"
            value={list.group || ''}
            onChange={this.handleFilterChange}
          >
            <option value="">{t('select_group', 'All groups')}</option>
            {groups.map((item) => (
              <option value={item.id} key={item.id}>
                {item.group_name}
              </option>
            ))}
          </select>
        </div>
        <button
          type="button"
          className="wlt-btn wlt-btn--ghost wlt-btn--sm"
          onClick={this.clearFilters}
          disabled={!this.hasAnyFilter()}
        >
          <X size={14} />
          {t('clear', 'Clear')}
        </button>
      </div>
    );
  }

  renderReceiptCell(row) {
    const files = getReceiptFiles(row);
    const first = files[0];

    return (
      <div className="wlt-receipt">
        {first ? (
          <button
            type="button"
            className="wlt-receipt__thumb"
            onClick={() => this.openPreview(row, files)}
            aria-label={t('view_receipt', 'View receipt')}
            title={t('view_receipt', 'View receipt')}
          >
            <FileText size={16} className="wlt-receipt__fallback" />
            {first.isImage && (
              <img
                src={first.url}
                alt=""
                loading="lazy"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
            )}
            {files.length > 1 && <span className="wlt-receipt__count">+{files.length - 1}</span>}
          </button>
        ) : (
          <span className="wlt-receipt__thumb is-empty" aria-hidden="true">
            <FileText size={16} />
          </span>
        )}
        <div className="wlt-receipt__text">
          <span className="wlt-num">
            {row.receipt_number ||
              (row.payment_receipts && row.payment_receipts[0]?.receipt_number) ||
              'Receipt'}
          </span>
          <span className="wlt-receipt__sub">
            {files.length
              ? `${files.length} ${files.length === 1 ? t('file', 'file') : t('files', 'files')}`
              : t('no_receipt', 'No receipt')}
          </span>
        </div>
      </div>
    );
  }

  renderRow(row) {
    const { Icon, tone } = typeVisual(row);
    const credit = toNumber(row.credit);
    const debit = toNumber(row.debit);
    const groupName = row.group && row.group.group_name;
    const auctionTitle =
      (row.auction_relation && row.auction_relation.title) ||
      (row.auctionRelation && row.auctionRelation.title);

    return (
      <tr key={row.id}>
        <td>
          <div className="wlt-tx">
            <span className={`wlt-tx__icon wlt-tone--${tone}`}>
              <Icon size={16} />
            </span>
            <div className="wlt-tx__text">
              <span className="wlt-tx__id wlt-num">{row.transaction_id || '—'}</span>
              <span className="wlt-tx__meta">
                #{row.id}
                {row.reference ? ` · Ref ${row.reference}` : ''}
              </span>
              {(auctionTitle || groupName) && (
                <span className="wlt-tx__meta">{auctionTitle || groupName}</span>
              )}
            </div>
          </div>
        </td>
        <td>{this.renderReceiptCell(row)}</td>
        <td className="is-num">
          {credit ? (
            <span className="wlt-num wlt-credit">+{fmt(credit)}</span>
          ) : (
            <span className="wlt-dash">—</span>
          )}
        </td>
        <td className="is-num">
          {debit ? (
            <span className="wlt-num wlt-debit">-{fmt(debit)}</span>
          ) : (
            <span className="wlt-dash">—</span>
          )}
        </td>
        <td className="wlt-type">
          {typeLabel(row)}
          {row.method && <span className="wlt-type__method">{methodLabel(row.method)}</span>}
        </td>
        <td>
          <span className={`wlt-pill wlt-pill--${statusTone(row)}`}>{statusLabel(row)}</span>
        </td>
        <td className="is-num wlt-num wlt-date">{formatDate(row.created_at)}</td>
      </tr>
    );
  }

  renderPagination() {
    const { list, totalRows, totalPages, isLoading } = this.state;
    const from = totalRows ? (list.page - 1) * list.limit + 1 : 0;
    const to = Math.min(list.page * list.limit, totalRows);
    const atStart = list.page <= 1 || isLoading;
    const atEnd = list.page >= totalPages || isLoading;

    return (
      <div className="wlt-pager">
        <label className="wlt-pager__size">
          {t('rows_per_page', 'Rows per page')}
          <select
            className="wlt-input wlt-input--sm"
            value={list.limit}
            onChange={this.handleLimitChange}
          >
            {ROWS_PER_PAGE_OPTIONS.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </label>

        <span className="wlt-pager__range wlt-num">
          {from}-{to} {t('of', 'of')} {totalRows}
        </span>

        <nav className="wlt-pager__nav" aria-label={t('pagination', 'Pagination')}>
          <button
            type="button"
            disabled={atStart}
            onClick={() => this.goToPage(1)}
            aria-label="First page"
          >
            <ChevronsLeft size={15} />
          </button>
          <button
            type="button"
            disabled={atStart}
            onClick={() => this.goToPage(list.page - 1)}
            aria-label="Previous page"
          >
            <ChevronLeft size={15} />
          </button>

          {pageItems(list.page, totalPages).map((item) =>
            typeof item === 'number' ? (
              <button
                key={item}
                type="button"
                className={`wlt-pager__page ${item === list.page ? 'is-current' : ''}`}
                aria-current={item === list.page ? 'page' : undefined}
                disabled={isLoading && item !== list.page}
                onClick={() => this.goToPage(item)}
              >
                {item}
              </button>
            ) : (
              <span key={item} className="wlt-pager__gap">
                …
              </span>
            )
          )}

          <button
            type="button"
            disabled={atEnd}
            onClick={() => this.goToPage(list.page + 1)}
            aria-label="Next page"
          >
            <ChevronRight size={15} />
          </button>
          <button
            type="button"
            disabled={atEnd}
            onClick={() => this.goToPage(totalPages)}
            aria-label="Last page"
          >
            <ChevronsRight size={15} />
          </button>
        </nav>
      </div>
    );
  }

  renderTableBody() {
    const { transactions, isLoading, loadError } = this.state;
    const COLS = 7;

    if (loadError && !isLoading) {
      return (
        <tr>
          <td colSpan={COLS} className="wlt-empty">
            <div className="wlt-empty__inner">
              <AlertTriangle size={28} />
              <span className="wlt-empty__text">{loadError}</span>
              <button
                type="button"
                className="wlt-btn wlt-btn--ghost wlt-btn--sm"
                onClick={this.refreshAll}
              >
                <RefreshCw size={14} />
                {t('retry', 'Retry')}
              </button>
            </div>
          </td>
        </tr>
      );
    }

    if (isLoading && !transactions.length) {
      return Array.from({ length: 5 }).map((_, i) => (
        <tr key={`sk-${i}`} className="wlt-skeleton-row">
          <td colSpan={COLS}>
            <span className="wlt-skeleton" />
          </td>
        </tr>
      ));
    }

    if (!transactions.length) {
      return (
        <tr>
          <td colSpan={COLS} className="wlt-empty">
            <WalletIcon size={28} />
            <span>
              {this.hasAnyFilter() ? t('no_transactions_filtered') : t('no_transactions')}
            </span>
            {this.hasAnyFilter() && (
              <button
                type="button"
                className="wlt-btn wlt-btn--ghost wlt-btn--sm"
                onClick={this.clearFilters}
              >
                {t('clear_filters')}
              </button>
            )}
          </td>
        </tr>
      );
    }

    return transactions.map((row) => this.renderRow(row));
  }

  renderTransactions() {
    const { transactions, totalRows, isLoading, showFilters, searchInput, exporting } = this.state;
    const filterCount = this.activeFilterCount();

    return (
      <div className="wlt-card">
        <div className="wlt-card__head">
          <div>
            <h3 className="wlt-card__title">{t('transaction_summary', 'Transaction Summary')}</h3>
            <span className="wlt-card__sub">
              {totalRows} {t('transactions', 'transactions')}
              {this.hasAnyFilter() && ` · ${t('filtered', 'filtered')}`}
            </span>
          </div>

          <div className="wlt-toolbar">
            <div className="wlt-search">
              <Search size={16} />
              <input
                type="text"
                name="search"
                maxLength={100}
                placeholder={t(
                  'search_transactions',
                  'Search transaction, reference or receipt no.'
                )}
                value={searchInput}
                onChange={this.handleSearchInput}
              />
              {searchInput && (
                <button
                  type="button"
                  className="wlt-search__clear"
                  onClick={this.clearSearch}
                  aria-label={t('clear', 'Clear')}
                >
                  <X size={14} />
                </button>
              )}
            </div>

            <button
              type="button"
              className={`wlt-btn wlt-btn--ghost ${showFilters || filterCount ? 'is-active' : ''}`}
              onClick={() => this.setState({ showFilters: !showFilters })}
              aria-expanded={showFilters}
            >
              <SlidersHorizontal size={15} />
              {t('filters', 'Filters')}
              {filterCount > 0 && <span className="wlt-badge">{filterCount}</span>}
            </button>
            <button
              type="button"
              className="wlt-btn wlt-btn--ghost"
              onClick={this.handleExport}
              disabled={exporting || !totalRows}
            >
              <Download size={15} />
              {exporting ? t('exporting', 'Exporting…') : t('export', 'Export')}
            </button>
          </div>
        </div>

        {this.renderActiveFilterBar()}
        {this.renderFilters()}

        <div className={`wlt-table-wrap ${isLoading ? 'is-loading' : ''}`} aria-busy={isLoading}>
          <table className="wlt-table">
            <thead>
              <tr>
                <th>{t('transaction')}</th>
                <th>{t('receipt')}</th>
                <th className="is-num">{t('credit')}</th>
                <th className="is-num">{t('debit')}</th>
                <th>{t('type')}</th>
                <th>{t('status')}</th>
                <th className="is-num">{t('date')}</th>
              </tr>
            </thead>
            <tbody>{this.renderTableBody()}</tbody>
          </table>
          {isLoading && transactions.length > 0 && (
            <div className="wlt-table-overlay">
              <span className="wlt-spinner" />
            </div>
          )}
        </div>

        {this.renderPagination()}
      </div>
    );
  }

  renderReceiptPreview() {
    const { preview } = this.state;
    const show = !!preview;
    const files = (preview && preview.files) || [];
    const index = (preview && preview.index) || 0;
    const file = files[index];
    const row = (preview && preview.row) || {};

    return (
      <Modal
        show={show}
        onHide={this.closePreview}
        centered
        size="lg"
        dialogClassName="wlt-modal wlt-preview"
      >
        {file && (
          <div className="wlt-modal__body">
            <div className="wlt-modal__head">
              <span className="wlt-stat__icon wlt-tone--teal">
                <FileText size={18} />
              </span>
              <div>
                <h4>
                  {t('receipt', 'Receipt')}
                  {row.receipt_number ? ` · ${row.receipt_number}` : ''}
                </h4>
                <p>
                  {file.label}
                  {files.length > 1 ? ` · ${index + 1} / ${files.length}` : ''}
                  {row.transaction_id ? ` · ${row.transaction_id}` : ''}
                </p>
              </div>
              <button
                type="button"
                className="wlt-modal__close"
                onClick={this.closePreview}
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>

            <div className="wlt-preview__stage">
              {files.length > 1 && (
                <button
                  type="button"
                  className="wlt-preview__nav is-prev"
                  onClick={() => this.stepPreview(-1)}
                  aria-label="Previous file"
                >
                  <ChevronLeft size={18} />
                </button>
              )}
              {file.isImage ? (
                <img src={file.url} alt={file.label} />
              ) : (
                <div className="wlt-preview__doc">
                  <FileText size={40} />
                  <span>
                    {t('preview_unavailable', 'Preview not available for this file type')}
                  </span>
                </div>
              )}
              {files.length > 1 && (
                <button
                  type="button"
                  className="wlt-preview__nav is-next"
                  onClick={() => this.stepPreview(1)}
                  aria-label="Next file"
                >
                  <ChevronRight size={18} />
                </button>
              )}
            </div>

            {files.length > 1 && (
              <div className="wlt-preview__strip">
                {files.map((f, i) => (
                  <button
                    key={f.url}
                    type="button"
                    className={`wlt-receipt__thumb ${i === index ? 'is-current' : ''}`}
                    onClick={() => this.setState({ preview: { ...preview, index: i } })}
                    aria-label={f.label}
                  >
                    <FileText size={16} className="wlt-receipt__fallback" />
                    {f.isImage && (
                      <img
                        src={f.url}
                        alt=""
                        loading="lazy"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                    )}
                  </button>
                ))}
              </div>
            )}

            <div className="wlt-modal__actions">
              <a
                className="wlt-btn wlt-btn--ghost"
                href={file.url}
                target="_blank"
                rel="noopener noreferrer"
              >
                <ExternalLink size={15} />
                {t('open_new_tab', 'Open in new tab')}
              </a>
              <a
                className="wlt-btn wlt-btn--primary"
                href={file.url}
                download
                target="_blank"
                rel="noopener noreferrer"
              >
                <Download size={15} />
                {t('download', 'Download')}
              </a>
            </div>
          </div>
        )}
      </Modal>
    );
  }

  renderWithdrawModal() {
    const { showWithdraw, input, errors, userInfo, isWithdrawSending } = this.state;
    if (!showWithdraw) return null;

    const remaining = toNumber(userInfo.wallet_amount) - toNumber(input.amount);
    const modalRoot = document.getElementById('modal-root') || document.body;

    const field = (name, label, props = {}) => (
      <div className="wlt-field">
        <label htmlFor={`wlt-${name}`}>
          {label} <span className="wlt-req">*</span>
        </label>
        <input
          id={`wlt-${name}`}
          type="text"
          className={`wlt-input ${errors[name] ? 'has-error' : ''}`}
          name={name}
          placeholder={label}
          value={input[name] || ''}
          onChange={this.handleChange}
          {...props}
        />
        {errors[name] && <small className="wlt-error">{errors[name]}</small>}
      </div>
    );

    return ReactDOM.createPortal(
      <div className="wlt-modal-overlay" onClick={this.closeWithdraw}>
        <form
          className="wlt-modal wlt-modal--portal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="wlt-withdraw-title"
          onSubmit={this.handleSubmit}
          onClick={(e) => e.stopPropagation()}
          autoComplete="off"
        >
          {/* Header */}
          <div className="wlt-modal__head">
            <span className="wlt-stat__icon wlt-tone--blue">
              <BanknoteArrowDownIcon size={18} />
            </span>
            <div>
              <h4 id="wlt-withdraw-title">{t('withdraw', 'Withdraw')}</h4>
              <p>{t('withdraw_display_message')}</p>
            </div>
            <button
              type="button"
              className="wlt-modal__close"
              onClick={this.closeWithdraw}
              aria-label="Close"
            >
              <X size={18} />
            </button>
          </div>

          {/* Body (scrolls if the screen is short) */}
          <div className="wlt-modal__scroll">
            <div className="wlt-modal__balance">
              <span>{t('available_balance', 'Available Balance')}</span>
              <strong className="wlt-num">
                {fmt(userInfo.wallet_amount)}{' '}
                <small>
                  <OMR size={12} color="#f6f6f6" />
                </small>
              </strong>
            </div>

            {field('bank', t('bank', 'Bank'))}
            {field('beneficiary', t('beneficiary', 'Beneficiary'))}
            {field('accountNumber', t('account_number', 'Account Number'), {
              inputMode: 'numeric',
            })}

            <div className="wlt-field">
              <label htmlFor="wlt-amount">
                {t('withdraw', 'Withdraw')} {t('amount', 'Amount')}{' '}
                <span className="wlt-req">*</span>
              </label>
              <div className="wlt-amount">
                <input
                  id="wlt-amount"
                  type="text"
                  inputMode="decimal"
                  className={`wlt-input wlt-num ${errors.amount ? 'has-error' : ''}`}
                  name="amount"
                  placeholder="0.00"
                  value={input.amount || ''}
                  onChange={this.handleChange}
                />
                <button type="button" className="wlt-amount__max" onClick={this.setMaxAmount}>
                  {t('max', 'Max')}
                </button>
              </div>
              {errors.amount && <small className="wlt-error">{errors.amount}</small>}
            </div>

            <div className="wlt-modal__remaining">
              <span>{t('balance_after', 'Balance after withdrawal')}</span>
              <strong className="wlt-num">
                {fmt(Math.max(0, remaining))} <OMR size={10} />
              </strong>
            </div>
          </div>

          {/* Footer */}
          <div className="wlt-modal__actions">
            <button
              type="button"
              className="wlt-btn wlt-btn--ghost"
              onClick={this.closeWithdraw}
              disabled={isWithdrawSending}
            >
              {t('cancel', 'Cancel')}
            </button>
            <button type="submit" className="wlt-btn wlt-btn--primary" disabled={isWithdrawSending}>
              {isWithdrawSending ? (
                <>
                  <span className="wlt-spinner wlt-spinner--light" />
                  {t('loading', 'Loading…')}
                </>
              ) : (
                <>
                  <ArrowUpRight size={16} />
                  {t('submit', 'Submit')}
                </>
              )}
            </button>
          </div>
        </form>
      </div>,
      modalRoot
    );
  }

  render() {
    const { hideSidebar } = this.state;

    return (
      <React.Fragment>
        <HeaderBanner title={t('my_wallet', 'My Wallet')} />
        <section className="dashboard-section">
          <div className="container">
            <div className="row">
              <div className="col-xl-3 col-lg-12 col-md-12">{!hideSidebar && <SideBar />}</div>

              <div
                className={
                  hideSidebar ? 'col-xl-12 col-lg-12 col-md-12' : 'col-xl-9 col-lg-12 col-md-12'
                }
              >
                <div className="wlt-page">
                  <div className="wlt-page__bar">
                    <div className="wlt-page__heading">
                      <span className="wlt-page__heading-icon">
                        <WalletIcon size={18} />
                      </span>
                      <div>
                        <h2>{t('my_wallet')}</h2>
                        <p>{t('wallet_subtitle')}</p>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="wlt-page__toggle"
                      onClick={() => this.setState({ hideSidebar: !hideSidebar })}
                      title={hideSidebar ? t('show_menu', 'Show menu') : t('expand', 'Expand')}
                    >
                      {hideSidebar ? <Shrink size={16} /> : <Expand size={16} />}
                      <span>
                        {hideSidebar ? t('show_menu', 'Show menu') : t('expand', 'Expand')}
                      </span>
                    </button>
                  </div>

                  <div className="wlt">
                    <div className="wlt-top">
                      {this.renderBalanceCard()}
                      <div className="wlt-stats">
                        {this.renderStat('deposit', 'green', WalletIcon, t('deposit'))}
                        {this.renderStat('auction_payment', 'amber', Gavel, t('auction_payment'))}
                        {this.renderStat('hold', 'red', Lock, t('on_hold'))}
                        {this.renderStat('withdraw', 'blue', BanknoteArrowDownIcon, t('withdraw'))}
                      </div>
                    </div>
                    {this.renderTransactions()}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        {this.renderWithdrawModal()}
        {this.renderReceiptPreview()}
      </React.Fragment>
    );
  }
}
